import { useEffect, useRef, useState } from 'react';
import { addDays, beijingDay, type AnalyticsSummary } from '@shared/analytics';
import { analyticsRequest } from './analyticsRequest';

export interface AnalyticsArchive {
  bucket: string;
  event: string;
  hits: number;
  visitors: number;
}
type Grain = 'day' | 'week';
export function useAnalyticsData() {
  const [query, setQuery] = useState({
    period: '7',
    grain: 'day' as Grain,
    revision: 0,
  });
  const [data, setData] = useState<AnalyticsSummary | null>(null);
  const [archives, setArchives] = useState<AnalyticsArchive[]>([]);
  const [loadedGrain, setLoadedGrain] = useState<Grain>('day');
  const [collecting, setCollecting] = useState<boolean | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(true);
  const generation = useRef(0);
  const active = useRef<AbortController | null>(null);
  const today = beijingDay();
  const to = query.period === 'yesterday' ? addDays(today, -1) : today;
  const from =
    query.period === 'yesterday' ? to : addDays(to, 1 - Number(query.period));

  // Invalidate synchronously in the event handler, before React runs cleanup.
  function change(next: Partial<typeof query>) {
    generation.current++;
    active.current?.abort();
    setQuery((previous) => ({
      ...previous,
      ...next,
      revision: previous.revision + 1,
    }));
  }
  useEffect(() => {
    const controller = new AbortController();
    active.current = controller;
    const id = ++generation.current;
    const current = () =>
      id === generation.current && !controller.signal.aborted;
    setBusy(true);
    setError('');
    setData(null);
    setArchives([]);
    void Promise.all([
      analyticsRequest<AnalyticsSummary>(
        `summary?from=${from}&to=${to}&grain=${query.grain}`,
        undefined,
        controller.signal,
      ),
      analyticsRequest<AnalyticsArchive[]>(
        `archives?grain=${query.grain}`,
        undefined,
        controller.signal,
      ),
    ])
      .then(([summary, old]) => {
        if (!current()) return;
        setData(summary);
        setArchives(old);
        setLoadedGrain(query.grain);
        setCollecting(summary.collecting);
      })
      .catch((e: unknown) => {
        if (current())
          setError(
            e instanceof Error ? e.message : '运营数据暂时无法读取，请重试',
          );
      })
      .finally(() => {
        if (current()) setBusy(false);
      });
    return () => {
      controller.abort();
      generation.current++;
    };
  }, [from, to, query.grain, query.revision]);
  return {
    period: query.period,
    grain: query.grain,
    from,
    to,
    data,
    archives,
    loadedGrain,
    collecting,
    error,
    busy,
    setPeriod: (period: string) => change({ period }),
    setGrain: (grain: Grain) => change({ grain }),
    refresh: () => change({}),
    confirmCollecting: (value: boolean) => {
      generation.current++;
      active.current?.abort();
      setCollecting(value);
    },
  };
}
